// 12.06.25 14:35 - Updated App.js for always-visible sidebar layout
import PWAInstallPrompt from './components/PWAInstallPrompt'; // 27.11.25 PWA
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';

// Navigation components
// 19.02.26 16:00 AEST - Re-enabled BottomNavigation import for mobile
import BottomNavigation from './components/navigation/BottomNavigation';
import SideDrawer from './components/navigation/SideDrawer';

// 03.09.25 19:30 AEST - Completed folder change for ALL auth components
// Date/Time: 03.09.25 19:30 AEST
// Comment: Fixed all auth import paths to match actual file locations after reorganization

import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import CustomPasswordReset from './pages/auth/CustomPasswordReset';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Main pages
import Dashboard from './pages/Dashboard';
import FilesPage from './pages/FilesPage';
import EmailsPage from './pages/EmailsPage';
import UploadPage from './pages/UploadPage';
import DoodlePage from './pages/DoodlePage'; // 12.06.25 14:35 - Added Drawing Practice page
import RemindersPage from './pages/RemindersPage'; // 10.09.25 09:35 AEST - Added RemindersPage component
import TestPage from './pages/TestPage';
import NoticeboardPage from './pages/NoticeboardPage'; // [TODAY'S DATE] - Added Noticeboard functionality
import GoalsPage from './pages/GoalsPage';
import AccountPage from './pages/AccountPage';
import SettingsPage from './pages/SettingsPage';
import MorePage from './pages/MorePage'; // 20.02.26 - More page for mobile nav
import AdminPage from './pages/AdminPage'; // 20.02.26 - Admin account management
import AccountSuspendedPage from './pages/AccountSuspendedPage'; // 20.02.26 - Suspended user page

// Test and Debug pages (keep for development)
import SimpleUploadPage from './pages/SimpleUploadPage';
import BasicUpload from './pages/BasicUpload';
import StorageRulesTest from './pages/StorageRulesTest';
import ConnectionTest from './pages/ConnectionTest';
import TestEmailCreation from './pages/TestEmailCreation';
import TestFunctionsPage from './pages/TestFunctionsPage';

import './base.css';

function App() {
  // 12.06.25 14:35 - Keep drawer state for mobile compatibility
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const closeDrawer = () => {
    setDrawerOpen(false);
  };
  
  return (
    <AuthProvider>
      <Router>
        <div className="app-container">
          {/* 12.06.25 14:35 - Always visible sidebar for authenticated users */}
          <Routes>
            {/* Auth Routes - No sidebar */}
            <Route path="/login" element={<Navigate to="/cooc-login.html" replace />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<CustomPasswordReset />} />

            {/* Test Route - No sidebar */}
            <Route path="/test" element={
              <div style={{ padding: '20px' }}>
                <h1>COOC Test Page</h1>
                <p>If you can see this, React is working!</p>
              </div>
            } />
            
{/* React Test Route */}
<Route path="/test-react" element={<TestPage />} />

            {/* Test and Debug Routes - No sidebar */}
            <Route path="/simple-upload" element={<SimpleUploadPage />} />
            <Route path="/basic-upload" element={<BasicUpload />} />
            <Route path="/storage-rules-test" element={<StorageRulesTest />} />
            <Route path="/test-connection" element={<ConnectionTest />} />
            <Route path="/test-email-creation" element={<TestEmailCreation />} />
            <Route path="/test-functions" element={<TestFunctionsPage />} />

            {/* 02.03.26 - Account suspended page OUTSIDE ProtectedRoute to avoid redirect loop */}
            <Route path="/account-suspended" element={<AccountSuspendedPage />} />

            {/* Protected Routes with Sidebar Layout */}
<Route
  path="/*"
  element={
    <ProtectedRoute>
      {/* 05.09.25 16:55 AEST - Inline fallback sizing to prevent layout collapse */}
      <div className="app-with-sidebar" style={{ display: 'flex', minHeight: '100vh' }}>
        {/* 12.06.25 14:35 - Always visible sidebar */}
        <SideDrawer isOpen={drawerOpen} onClose={closeDrawer} />

        {/* 12.06.25 14:35 - Main content area with left margin for sidebar */}
        {/* 05.09.25 16:55 AEST - Main pane flex rules (prevents 0×0, enables scrolling) */}
        <main
          className="main-content-with-sidebar"
          style={{ flex: 1, minWidth: 0, minHeight: '100vh', overflow: 'auto' }}
        >
          <Routes>
            <Route path="/" element={<Dashboard toggleDrawer={toggleDrawer} />} />
            {/* 05.09.25 16:25 - Also map /dashboard for login redirect */}
            <Route path="/dashboard" element={<Dashboard toggleDrawer={toggleDrawer} />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/emails" element={<EmailsPage />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/doodle" element={<DoodlePage />} />

            {/* Placeholder pages */}
            <Route path="/noticeboard" element={<NoticeboardPage />} />
           {/* 10.09.25 09:35 AEST - Connected RemindersPage component */}
           <Route
              path="/reminders"
              element={<RemindersPage />}
            />
           <Route path="/goals" element={<GoalsPage />} />
           
            <Route
              path="/news"
              element={
                <div style={{ padding: '20px' }}>
                  <h1>News Feed</h1>
                  <p>Coming soon...</p>
                </div>
              }
            />
<Route path="/account" element={<AccountPage />} />
<Route path="/settings" element={<SettingsPage />} />
<Route path="/more" element={<MorePage />} /> {/* 20.02.26 - More page for mobile nav */}
<Route path="/admin" element={<AdminPage />} /> {/* 20.02.26 - Admin panel */}

```

---

Once done, don't forget the Firestore step before deploying — go to Firebase Console → Firestore → create collection `Admins` → add document with ID `tom@cooc.com.au` → add field `role` = `admin`. Without this the admin functions will reject your calls.

Then deploy in order:
```
firebase deploy --only functions
npm run build:app
firebase deploy --only hosting

{/* Fallback route */}
<Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* 27.11.25 15:30 AEDT - PWA Install Prompt */}
        <PWAInstallPrompt />

        </div>
      {/* 20.02.26 - Moved BottomNavigation outside app-with-sidebar div so
          position:fixed isn't trapped by parent stacking context */}
      <BottomNavigation />
    </ProtectedRoute>
  }
/>
           
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;